import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect } from "react";
import {
  Bold, Italic, List, ListOrdered, Link2, Heading1, Heading2,
  Undo, Redo, Quote, Code, Minus,
} from "lucide-react";

const btnCls = "inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition";
const btnActiveCls = "bg-slate-200 text-slate-900";

function ToolbarBtn({ onClick, active, title, disabled, children, testid }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`${btnCls} ${active ? btnActiveCls : ""} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

/** TipTap-based WYSIWYG for HTML email bodies.
 * Preserves Jinja2 tokens like `{{ staff_name }}` — the editor treats them
 * as literal text. Emits sanitized HTML on every change. */
export default function TipTapEditor({ value, onChange, placeholder, testid = "tiptap" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-[#1E3A8A] underline" } }),
      Placeholder.configure({ placeholder: placeholder || "Compose the email body…" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none min-h-[280px] p-3 focus:outline-none",
        "data-testid": `${testid}-body`,
      },
    },
  });

  // Sync incoming `value` when parent resets (e.g., "Reset to catalog default").
  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const url = window.prompt("URL (leave empty to remove):", editor.getAttributes("link").href || "");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  };

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden bg-white" data-testid={testid}>
      <div className="flex flex-wrap gap-1 p-2 border-b border-slate-200 bg-slate-50">
        <ToolbarBtn testid={`${testid}-bold`}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    active={editor.isActive("bold")} title="Bold"><Bold size={14} /></ToolbarBtn>
        <ToolbarBtn testid={`${testid}-italic`}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    active={editor.isActive("italic")} title="Italic"><Italic size={14} /></ToolbarBtn>
        <ToolbarBtn testid={`${testid}-h1`}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    active={editor.isActive("heading", { level: 1 })} title="H1"><Heading1 size={14} /></ToolbarBtn>
        <ToolbarBtn testid={`${testid}-h2`}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    active={editor.isActive("heading", { level: 2 })} title="H2"><Heading2 size={14} /></ToolbarBtn>
        <ToolbarBtn testid={`${testid}-bullet`}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    active={editor.isActive("bulletList")} title="Bullet list"><List size={14} /></ToolbarBtn>
        <ToolbarBtn testid={`${testid}-numbered`}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    active={editor.isActive("orderedList")} title="Numbered list"><ListOrdered size={14} /></ToolbarBtn>
        <ToolbarBtn testid={`${testid}-quote`}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    active={editor.isActive("blockquote")} title="Quote"><Quote size={14} /></ToolbarBtn>
        <ToolbarBtn testid={`${testid}-code`}
                    onClick={() => editor.chain().focus().toggleCode().run()}
                    active={editor.isActive("code")} title="Inline code"><Code size={14} /></ToolbarBtn>
        <ToolbarBtn testid={`${testid}-hr`}
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    title="Divider"><Minus size={14} /></ToolbarBtn>
        <ToolbarBtn testid={`${testid}-link`}
                    onClick={setLink}
                    active={editor.isActive("link")} title="Link"><Link2 size={14} /></ToolbarBtn>
        <div className="flex-1" />
        <ToolbarBtn testid={`${testid}-undo`}
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()} title="Undo"><Undo size={14} /></ToolbarBtn>
        <ToolbarBtn testid={`${testid}-redo`}
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()} title="Redo"><Redo size={14} /></ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
